function defaultHash() {
  return '#/systems';
}

function findingsFiltersFromQuery(query) {
  return {
    severity: query.get('severity'),
    status: query.get('status'),
    prevalence: query.get('prevalence'),
    workflowId: query.get('workflow_id'),
    componentId: query.get('component_id'),
  };
}

function runtimeCoverageFiltersFromQuery(query) {
  return {
    workflowId: query.get('workflow_id'),
    findingStatus: query.get('finding_status'),
    findingPriority: query.get('finding_priority'),
    coverageStatus: query.get('coverage_status'),
    attentionOnly: query.get('attention_only') === 'true',
  };
}

export function parseRoute(hash) {
  const cleaned = hash.replace(/^#/, '') || defaultHash().replace(/^#/, '');
  const [pathPart, queryPart] = cleaned.split('?');
  const parts = pathPart.split('/').filter(Boolean);
  const query = new URLSearchParams(queryPart || '');

  if (parts[0] === 'how-rti-works') {
    return { name: 'how-rti-works' };
  }
  if (parts[0] === 'access') {
    return { name: 'access' };
  }
  if (parts[0] !== 'systems') {
    return { name: 'systems-list', systemId: null, focusComponentId: query.get('component') };
  }
  if (!parts[1]) {
    return { name: 'systems-list', systemId: null, focusComponentId: query.get('component') };
  }
  if (parts[1] === 'onboard') {
    return { name: 'systems-onboard' };
  }
  if (!parts[2] || parts[2] === 'overview') {
    return { name: 'overview', systemId: parts[1], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'architecture') {
    return { name: 'architecture', systemId: parts[1], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'compare') {
    return {
      name: 'compare',
      systemId: parts[1],
      compareTab: query.get('tab') || 'findings',
      focusComponentId: query.get('component'),
    };
  }
  if (parts[2] === 'runtime-coverage') {
    return {
      name: 'runtime-coverage',
      systemId: parts[1],
      focusComponentId: query.get('component'),
      ...runtimeCoverageFiltersFromQuery(query),
    };
  }
  if (parts[2] === 'integrations') {
    return { name: 'integrations', systemId: parts[1], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'workflows' && parts[3]) {
    return { name: 'workflow-detail', systemId: parts[1], workflowId: parts[3], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'eval-packs' && parts[3]) {
    return { name: 'eval-pack-detail', systemId: parts[1], packId: parts[3], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'runs' && parts[3] && parts[4] === 'trace') {
    return {
      name: 'run-trace-detail',
      systemId: parts[1],
      runId: parts[3],
      primaryStepId: query.get('primary_step_id'),
      comparisonStepIds: query.getAll('comparison_step_id').filter(Boolean),
      sourceFindingId: query.get('source_finding_id'),
      focusComponentId: query.get('component'),
    };
  }
  if (parts[2] === 'workflows') {
    return { name: 'workflows-list', systemId: parts[1], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'components' && parts[3]) {
    return { name: 'component-detail', systemId: parts[1], componentId: parts[3], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'components') {
    return { name: 'components-list', systemId: parts[1], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'findings' && parts[3]) {
    return {
      name: 'finding-detail',
      systemId: parts[1],
      findingId: parts[3],
      focusComponentId: query.get('component'),
      section: query.get('section'),
    };
  }
  if (parts[2] === 'recommendations' && parts[3]) {
    return { name: 'recommendation-detail', systemId: parts[1], recommendationId: parts[3], focusComponentId: query.get('component') };
  }
  if (parts[2] === 'findings') {
    return {
      name: 'findings-list',
      systemId: parts[1],
      focusComponentId: query.get('component'),
      ...findingsFiltersFromQuery(query),
    };
  }
  return { name: 'systems-list', systemId: parts[1], focusComponentId: query.get('component') };
}

export function routeTitle(route) {
  switch (route.name) {
    case 'systems-onboard':
      return 'New system onboarding';
    case 'access':
      return 'Access';
    case 'how-rti-works':
      return 'How RTI works';
    case 'overview':
      return 'Evidence overview';
    case 'architecture':
      return 'System architecture';
    case 'components-list':
      return 'Component health';
    case 'component-detail':
      return 'Component detail';
    case 'compare':
      return 'Outcome proof';
    case 'runtime-coverage':
      return 'Evaluation coverage';
    case 'integrations':
      return 'Managed integrations';
    case 'workflows-list':
      return 'Workflow surfaces';
    case 'workflow-detail':
      return 'Workflow detail';
    case 'eval-pack-detail':
      return 'Validation pack';
    case 'run-trace-detail':
      return 'Run trace';
    case 'findings-list':
      return 'Triage findings';
    case 'finding-detail':
      return 'Finding review';
    case 'recommendation-detail':
      return 'Recommendation detail';
    case 'systems-list':
    default:
      return 'Reliability intelligence';
  }
}

export function routeSubtitle(route) {
  switch (route.name) {
    case 'systems-onboard':
      return 'Create a system, upload a trace, and move directly into analysis.';
    case 'access':
      return 'Manage workspace access, active tenants, and audit activity.';
    case 'how-rti-works':
      return 'Understand what Runtime Reliability Intelligence analyzes, how findings are produced, and how to interpret runtime summaries, confidence, and validation.';
    case 'overview':
      return 'Start with runtime evidence, freshness, and the next reliability-review step.';
    case 'architecture':
      return 'Explore the runtime-derived component map and understand where reliability issues concentrate.';
    case 'components-list':
      return 'Review the components carrying the most reliability pain, together with health signals, mapping provenance, and linked findings.';
    case 'component-detail':
      return 'Inspect one component using persisted health signals, linked findings, workflow impact, and related architecture context.';
    case 'compare':
      return 'Compare runtime movement, validation proof, and low-data caveats without overstating outcome certainty.';
    case 'runtime-coverage':
      return 'Track reviewed runtime risks by evaluation coverage state without turning runtime evidence into release readiness.';
    case 'integrations':
      return 'Manage inbound sources, investigation links, and outbound handoff destinations without expanding the trust boundary.';
    case 'workflows-list':
      return 'Review the workflows that matter most, with bounded run-status trends, runtime patterns, and direct links into findings and traces.';
    case 'workflow-detail':
      return 'Inspect one workflow using persisted analysis context, related findings, and deterministic trace picks.';
    case 'eval-pack-detail':
      return 'Inspect a prepared targeted validation pack without confusing preparedness with validation coverage.';
    case 'run-trace-detail':
      return 'Inspect a persisted runtime trace with focused step hints carried from workflow and validation surfaces.';
    case 'findings-list':
      return 'Triage the issues that matter most, then review candidate scenarios before coverage handoff.';
    case 'finding-detail':
      return 'Review runtime evidence, expected behavior, handoff readiness, and coverage state for a single finding.';
    case 'recommendation-detail':
      return 'Turn a detected issue into an action plan with implementation guidance and expected benefit.';
    case 'systems-list':
    default:
      return 'Track readiness, findings, validation work, and improvement proof across each system.';
  }
}

export { defaultHash };
